import React, { useContext, useCallback, useState, useEffect } from "react";
import { Stack } from "@mui/material";

import { MeetingInfoContext } from "../../context";
import AnnotationGrid from "./components/annotation-grid";
import AnnotationControlBar from "./components/annotation-control-bar";

// Import types and utilities
import { AnnotationMessage, MessageType } from "./types/annotation-types";
import { annotationColors, } from "./utils/color-utils";

// Import custom hooks
import { useAnnotationState } from "./hooks/useAnnotationState";
import { useAnnotationMessages } from "./hooks/useAnnotationMessages";
import { UserJoinInformation } from "../../types";

interface LiveAnnotatorProps {
  joinInformation: UserJoinInformation
  viewWidth: number;
  viewHeight: number;
}

const LiveAnnotator: React.FC<LiveAnnotatorProps> = ({
  joinInformation,
  viewWidth,
  viewHeight,
}) => {

  const {
    userId,
    canAnnotate,
    showAnnotation,
    annotationUrlIndex,
  } = useContext(MeetingInfoContext);

  const [requestPoints, setRequestPoints] = useState<string[]>([])

  const addToRequestPoints = useCallback((sender: string) => {
    setRequestPoints((prev) => {
      if (prev.includes(sender)) {
        return prev
      }
      return [...prev, sender]
    })
  }, [])

  // Use custom hooks for state management
  const {
    pState,
    lineIndex,
    color,
    canReset,
    setLineIndex,
    setColor,
    clearUserAnnotations,
    undoLastLine,
    updateUserMouse,
    addPointToUser,
    onMessage,
  } = useAnnotationState({
    userId,
    userJoinInfo: joinInformation,
    addToRequestPoints,
  });

  const { updateMouseBuffer, sendAnnotationMessage, } =
    useAnnotationMessages({
      joinInfo: joinInformation,
      onMessage,
    });

  // Handle request points responses
  useEffect(() => {
    if (requestPoints.length === 0) {
      return
    }

    requestPoints.forEach((target) => {
      const message: AnnotationMessage = {
        sender: userId,
        type: MessageType.SendPoints,
        drawing: pState[userId] || {
          userMouse: null,
          points: [],
          color,
        },
        target,
        active: showAnnotation,
        url: annotationUrlIndex,
      }
      sendAnnotationMessage(message)
    })

    setRequestPoints([])
  }, [requestPoints, userId, sendAnnotationMessage])

  const sendPointAddedMessage = useCallback(
    (x: number, y: number, newLine: boolean) => {
      if (!canAnnotate) {
        console.error("~~ivai - Not connected, cannot annotate");
        return;
      }

      let index = lineIndex;

      if (newLine || index === -1) {
        index++;
        setLineIndex(index);
      }

      let startIndex = 0;
      if (pState[userId] && index < pState[userId].points.length) {
        startIndex = pState[userId].points[index].length;
      }

      // Send the point immediately for now (can be optimized with buffering later)
      const message: AnnotationMessage = {
        sender: userId,
        type: MessageType.Annotation,
        points: [{ x, y }],
        color,
        lineIndex: index,
        startIndex,
      }

      sendAnnotationMessage(message)

      addPointToUser(userId, x, y, color, index);
    },
    [
      canAnnotate,
      lineIndex,
      setLineIndex,
      pState,
      userId,
      sendAnnotationMessage,
      addPointToUser,
      color,
    ]
  );

  const handleColorChange = useCallback(
    (newColor: string) => {
      if (!canAnnotate) {
        console.error("~~ivai - Not connected, cannot annotate");
        return;
      }

      const message: AnnotationMessage = {
        sender: userId,
        type: MessageType.Color,
        color: newColor
      }
      sendAnnotationMessage(message)

      setColor(newColor, userId);
    },
    [canAnnotate, sendAnnotationMessage, setColor, userId]
  );

  const handleClearOnClick = useCallback(() => {
    if (!canAnnotate) {
      console.error("~~ivai - Not connected, cannot annotate");
      return;
    }
    const message: AnnotationMessage = {
      sender: userId,
      type: MessageType.Clear
    }

    sendAnnotationMessage(message)

    clearUserAnnotations(message);
    setLineIndex(-1);
  }, [
    canAnnotate,
    sendAnnotationMessage,
    clearUserAnnotations,
    userId,
    setLineIndex,
  ]);

  const handleUndoOnClick = useCallback(() => {
    if (!canAnnotate) {
      console.error("~~ivai - Not connected, cannot annotate");
      return;
    }

    const message: AnnotationMessage = {
      sender: userId,
      type: MessageType.Undo
    }

    sendAnnotationMessage(message)

    undoLastLine(message);
    setLineIndex((prevState) => prevState - 1);
  }, [canAnnotate, sendAnnotationMessage, undoLastLine, userId, setLineIndex]);

  const onMouseMove = useCallback(
    (x: number, y: number) => {
      if (!canAnnotate) {
        return;
      }

      updateUserMouse(userId, x, y, color);
      updateMouseBuffer({ x, y });
    },
    [canAnnotate, updateUserMouse, userId, color, updateMouseBuffer]
  );

  return (
    <Stack spacing={2}>
      {showAnnotation && (
        <AnnotationControlBar
          color={color}
          colorOptions={annotationColors}
          handleColorChange={handleColorChange}
          onReset={handleClearOnClick}
          onUndo={handleUndoOnClick}
          canReset={canReset}
        />
      )}
      <AnnotationGrid
        points={pState}
        viewHeight={viewHeight}
        viewWidth={viewWidth}
        onClick={sendPointAddedMessage}
        onMouseMove={onMouseMove}
      />
    </Stack>
  );
};

export default LiveAnnotator;
